import { useCallback, useState } from 'react'
import { Effect } from 'effect'
import { v7 as uuidv7 } from 'uuid'
import { datadogLogs } from '@datadog/browser-logs'
import type { Bet } from 'src/types/domain/Bet'
import type { BetDetails } from 'src/types/domain/BetDetails'
import type { MarbleGame } from 'src/types/domain/Game'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import { BetMap } from 'src/types/mappers/BetMap'
import { generateBetId } from 'src/utils/betUtils'
import { isBalanceReady } from 'src/utils/betGuards'
import { useBetActions } from 'src/store/bet'
import { usePlayerActions } from 'src/store/player'
import { useSocketStore } from 'src/store/socket'
import { useSoundEffect } from 'src/hooks/useSoundEffect'

interface UseCrashBetSubmitProps {
  game: MarbleGame
  player: AuthenticatedPlayer
  slotIndex: 0 | 1
}

/**
 * The submit outcome (v12.4 ask 7, G8). `aborted` distinguishes "the bet
 * never made it onto the wire for transport reasons" (no channel / channel
 * never joined / throw) from a plain validation no-op — the caller surfaces
 * an abort to the player ("couldn't place — holding for next round") while
 * validation no-ops stay silent (nothing was meant to go out).
 */
export interface CrashBetSubmitOutcome {
  betId?: string
  aborted: boolean
}

// How long to wait for a late channel join before aborting the submit
// (mirrors useChipPlacementBetSubmit — covers submission firing moments
// before a race_starting reconnect settles).
const CHANNEL_JOIN_DEADLINE_MS = 750

/**
 * A hard round's send (hard close rev 4, DESIGN §6): never on an unjoined
 * channel, and the join is waited for until the board's estimate of K − 0.5 s
 * (`joinDeadline`, `performance.now()` ms) instead of the fixed 750 ms. Past
 * it nothing is sent and the stake carries.
 */
export interface CrashBetSubmitOptions {
  joinDeadline?: number
}

const perfNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

export function useCrashBetSubmit({ game, player, slotIndex }: UseCrashBetSubmitProps) {
  const [isPlacingBet, setIsPlacingBet] = useState(false)
  const betActions = useBetActions()
  const playerActions = usePlayerActions()
  const { playEffect } = useSoundEffect()

  const submitBet = useCallback(
    async (
      amount: string,
      autoCashoutMultiplier?: number,
      options?: CrashBetSubmitOptions
    ): Promise<CrashBetSubmitOutcome> => {
      // Safety choke point: never submit a bet before the real balance has loaded
      // (non-blocking launch). Defense-in-depth — callers already gate on this.
      if (!isBalanceReady(player)) return { betId: undefined, aborted: false }
      setIsPlacingBet(true)
      try {
        // Resolve the channel FRESH from the store on every read (G8) — never
        // a render-closed-over reference. On reconnect the socket store swaps
        // in a NEW player channel; a stale reference pushes into a dead
        // channel whose pushes silently drop ("bet:accepted never arrives").
        // This matters doubly here: the armed race_starting submit timer holds
        // this callback across a 1.5s window.
        const getChannel = () => useSocketStore.getState().playerChannel
        if (!getChannel()) {
          datadogLogs.logger.warn('crash_bet_submit_aborted', {
            reason: 'no_channel',
            slotIndex
          })
          return { betId: undefined, aborted: true }
        }

        // Phoenix pushes silently queue on a non-joined channel and can be
        // dropped on reconnect. Wait briefly for a late join before giving
        // up (parity with useChipPlacementBetSubmit).
        const isJoined = () => {
          const ch = getChannel()
          return !!ch && (ch.state as string) === 'joined'
        }
        if (!isJoined()) {
          const hardDeadline = options?.joinDeadline
          const deadline = Date.now() + CHANNEL_JOIN_DEADLINE_MS
          const waiting = () =>
            hardDeadline === undefined ? Date.now() < deadline : perfNow() < hardDeadline
          while (!isJoined() && waiting()) {
            await new Promise<void>((r) => {
              window.setTimeout(r, 50)
            })
          }
          if (!isJoined()) {
            datadogLogs.logger.warn('crash_bet_submit_aborted', {
              reason: 'channel_not_joined',
              channelState: getChannel()?.state,
              slotIndex
            })
            return { betId: undefined, aborted: true }
          }
        }

        const currentRace = game.races[0]

        // Auto-select first active marble for crash games
        const firstActiveMarble = game.marbles.find((m) => m.active)
        if (!firstActiveMarble) {
          console.error('Error placing crash bet: no active marble')
          return { betId: undefined, aborted: false }
        }

        const betAmount = parseFloat(amount)
        if (isNaN(betAmount) || betAmount <= 0) {
          return { betId: undefined, aborted: false }
        }

        // Include slotIndex as nonce so both slots produce unique deterministic
        // bet IDs even when the amount and marble selection are the same.
        // The backend includes nonce in its ID hash, so IDs match on both sides.
        const betWithoutId: Omit<Bet, 'id'> = {
          playerId: player.id,
          raceId: currentRace.uuid,
          raceConfigurationId: currentRace.raceConfigurationId,
          betAmount,
          currency: player.balance.currency,
          betDetails: {
            type: 'PickWinner',
            selectedItems: [firstActiveMarble.uuid],
            ...(slotIndex > 0 && { nonce: `slot${slotIndex}` })
          } as BetDetails,
          status: 'pending',
          potentialPayout: 0,
          createdAt: new Date().toISOString(),
          ...(autoCashoutMultiplier !== undefined && { autoCashoutMultiplier })
        }

        const bet: Bet = {
          id: Effect.runSync(generateBetId(betWithoutId)),
          ...betWithoutId
        }

        const correlationId = uuidv7()
        const payload = BetMap.toPlaceBetRequestedDTO(player.sessionId, bet, correlationId)

        datadogLogs.logger.info('crash_bet_submit', {
          user_event: {
            payload,
            correlationId
          }
        })

        // Resolved fresh AFTER the join wait — a reconnect during the wait
        // must not leave us pushing on the pre-wait reference.
        const channel = getChannel()
        if (!channel) {
          datadogLogs.logger.warn('crash_bet_submit_aborted', {
            reason: 'channel_lost_after_join_wait',
            slotIndex
          })
          return { betId: undefined, aborted: true }
        }
        channel.push('client:bet:initiated', payload)

        betActions.betPlaced(bet)
        playerActions.betPlaced(bet, false)
        playEffect('betPlaced')

        return { betId: bet.id, aborted: false }
      } catch (error) {
        console.error('Placing crash bet failed:', error)
        datadogLogs.logger.warn('crash_bet_submit_aborted', {
          reason: 'exception',
          error: error instanceof Error ? error.message : String(error),
          slotIndex
        })
        return { betId: undefined, aborted: true }
      } finally {
        setIsPlacingBet(false)
      }
    },
    [game, player, slotIndex, betActions, playerActions, playEffect]
  )

  return { submitBet, isPlacingBet }
}
